import React, { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Auth Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-5">
          <div className="bg-white p-10 rounded-xl shadow-lg text-center max-w-md">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="m-0 mb-4 text-red-500 text-2xl font-semibold">
              Authentication Error
            </h2>
            <p className="m-0 mb-6 text-gray-600 leading-relaxed">
              There was a problem with the authentication system. Please check
              your environment configuration and try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-500 text-white border-none rounded-md text-base font-medium cursor-pointer transition-colors hover:bg-blue-600"
            >
              Reload Page
            </button>
            {this.state.error && (
              <details className="mt-6 text-left bg-gray-100 p-4 rounded-md">
                <summary className="cursor-pointer font-medium">
                  Error Details
                </summary>
                <pre className="mt-3 text-xs text-gray-600 whitespace-pre-wrap break-words">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
